import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FILTER_TYPE_META, FILTER_TYPE_ORDER } from '../../constants/reviewEntriesFilterConfig'
import { ReviewEntriesFilterType } from '../../enums/reviewEntriesFilterType'
import type { ReviewEntriesFiltersState, ReviewEntriesFilterValue } from '../../types/reviewEntriesFilters'
import type { ReviewEntryTableRow } from '../../types/reviewEntryTableRow'
import { AccountFilterChip } from './filters/AccountFilterChip'
import { AmountRangeFilterChip } from './filters/AmountRangeFilterChip'
import { SupplierFilterChip } from './filters/SupplierFilterChip'
import { VatRateFilterChip } from './filters/VatRateFilterChip'
import { ReviewEntriesAddFilterMenu, ReviewEntriesResetFiltersButton } from './ReviewEntriesFilterChipShell'
import { FiltersBarRow, FiltersBarSpacer } from './styles'

interface ReviewEntriesFiltersBarProps {
  addedTypes: ReviewEntriesFilterType[]
  autoOpenFilterType: ReviewEntriesFilterType | null
  filters: ReviewEntriesFiltersState
  isVisible: boolean
  rows: ReviewEntryTableRow[]
  addFilter: (type: ReviewEntriesFilterType) => void
  clearAutoOpenFilterType: () => void
  removeFilter: (type: ReviewEntriesFilterType) => void
  resetAllFilters: () => void
  resetFilter: (type: ReviewEntriesFilterType) => void
  setFilterValue: <T extends ReviewEntriesFilterType>(type: T, value: ReviewEntriesFilterValue<T>) => void
}

export const ReviewEntriesFiltersBar = ({
  addedTypes,
  autoOpenFilterType,
  filters,
  isVisible,
  rows,
  addFilter,
  clearAutoOpenFilterType,
  removeFilter,
  resetAllFilters,
  resetFilter,
  setFilterValue,
}: ReviewEntriesFiltersBarProps): ReactElement | null => {
  const { t } = useTranslation()

  const availableTypes = useMemo(() => FILTER_TYPE_ORDER.filter((type) => !addedTypes.includes(type)), [addedTypes])

  const getTypeLabel = useCallback((type: ReviewEntriesFilterType) => t(FILTER_TYPE_META[type].labelKey), [t])

  if (!isVisible) {
    return null
  }

  const renderChip = (type: ReviewEntriesFilterType): ReactElement => {
    const sharedProps = {
      shouldAutoOpen: autoOpenFilterType === type,
      onAutoOpenHandled: clearAutoOpenFilterType,
      onDelete: () => removeFilter(type),
      onReset: () => resetFilter(type),
    }

    switch (type) {
      case ReviewEntriesFilterType.Supplier:
        return (
          <SupplierFilterChip
            key={type}
            {...sharedProps}
            filters={filters}
            onChange={(values) => setFilterValue(type, values)}
            rows={rows}
          />
        )
      case ReviewEntriesFilterType.Account:
        return (
          <AccountFilterChip
            key={type}
            {...sharedProps}
            filters={filters}
            onChange={(values) => setFilterValue(type, values)}
            rows={rows}
          />
        )
      case ReviewEntriesFilterType.VatRate:
        return (
          <VatRateFilterChip
            key={type}
            {...sharedProps}
            filters={filters}
            onChange={(values) => setFilterValue(type, values)}
          />
        )
      case ReviewEntriesFilterType.ExclVat:
      case ReviewEntriesFilterType.InclVat:
        return (
          <AmountRangeFilterChip
            key={type}
            {...sharedProps}
            filters={filters}
            onChange={(range) => setFilterValue(type, range)}
            type={type}
          />
        )
      default:
        return <></>
    }
  }

  return (
    <FiltersBarRow>
      {addedTypes.map(renderChip)}
      <ReviewEntriesAddFilterMenu availableTypes={availableTypes} getTypeLabel={getTypeLabel} onSelect={addFilter} />
      <FiltersBarSpacer />
      <ReviewEntriesResetFiltersButton onClick={resetAllFilters} />
    </FiltersBarRow>
  )
}
